import { useEffect, useState } from 'react';

const STORAGE_KEY = 'cookieConsent';

// Bottom-anchored cookie banner styled after the "עוגיות לכולם.ן / كوكيز للجميع"
// design (Frame 8 desktop / Frame 14 mobile): flat purple panel, the
// raised fist multiply-blended into the corner, the headline lockup — Arabic
// in big white letters over the Hebrew line on a straight white band — and a
// white accept bar. Shown once until the visitor accepts or dismisses it.
export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Dev aid: on localhost, always show the banner on every reload so it's
    // easy to see and test. Real hosts still respect a stored choice.
    const isLocalhost = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
    if (isLocalhost) {
      setOpen(true);
      return;
    }
    try {
      // Skip if the visitor already chose here (localStorage) or on any
      // *.standing-together.org subdomain (Cookiebot's root-domain cookie).
      const chosenHere = localStorage.getItem(STORAGE_KEY);
      const chosenViaCookiebot = /(?:^|;\s*)CookieConsent=/.test(document.cookie);
      if (!chosenHere && !chosenViaCookiebot) setOpen(true);
    } catch {
      // Storage blocked (private mode / cookies off) — show the banner anyway.
      setOpen(true);
    }
  }, []);

  const close = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore — nothing we can persist */
    }
    // Forward the choice to Cookiebot (engine-only, its own dialog is hidden)
    // so data-blockingmode="auto" releases — or keeps blocking — the gated
    // trackers, and the consent is shared across subdomains. No-op when the
    // script is absent/blocked (e.g. placeholder cbid in dev).
    const cb = window.Cookiebot;
    if (cb && typeof cb.submitCustomConsent === 'function') {
      const accepted = value === 'accepted';
      cb.submitCustomConsent(accepted, accepted, accepted);
    }
    setOpen(false);
  };

  // "לחצו כאן": hand off to Cookiebot's own settings dialog. Cookiebot (v4)
  // runs engine-only here — its banner/dialog (#CybotCookiebotDialog) is force-
  // hidden by default (index.css) so only our fist banner shows. Reveal it via
  // body.show-cookiebot, (re)open it with renew(), and close our banner. Re-hide
  // it once the visitor accepts/declines so no empty shell is left behind.
  //
  // Only works on a domain AUTHORIZED for this CBID in the Cookiebot Manager;
  // unauthorized hosts (e.g. localhost) render no dialog, so the link no-ops.
  const openCookieSettings = () => {
    const cb = window.Cookiebot;
    if (!cb || typeof cb.renew !== 'function') return;

    const cleanup = () => {
      document.body.classList.remove('show-cookiebot');
      window.removeEventListener('CookiebotOnAccept', cleanup);
      window.removeEventListener('CookiebotOnDecline', cleanup);
    };
    window.addEventListener('CookiebotOnAccept', cleanup);
    window.addEventListener('CookiebotOnDecline', cleanup);

    document.body.classList.add('show-cookiebot');
    cb.renew();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="cookie-overlay"
      onClick={(e) => {
        // Click on the backdrop itself (not the banner/legal note) dismisses,
        // which counts as consent like the ✕ button.
        if (e.target === e.currentTarget) close('accepted');
      }}
    >
      <div className="cookie-banner" role="dialog" aria-modal="true" aria-label="עוגיות ملفات تعريف الارتباط">
      <button
        type="button"
        className="cookie-close"
        aria-label="סגירה إغلاق"
        onClick={() => close('accepted')} // closing with ✕ counts as consent; only the settings link opts out
      >
        ✕
      </button>

      <img className="cookie-fist" src="/images/cookie-fist.webp" alt="" aria-hidden="true" />

      {/* Per-word spans: desktop shows two lines (Arabic over the Hebrew band);
          mobile (Frame 14) interleaves the words كوكيز / עוגיות / לכולם.ן / للجميع
          via display: contents + order. */}
      <h2 className="cookie-headline">
        <span className="cookie-headline-ar">
          <span className="chw chw-kukiz">كوكيز</span> <span className="chw chw-liljamie">للجميع</span>
        </span>
        <span className="cookie-headline-he">
          <span className="chw chw-ugiyot">עוגיות</span> <span className="chw chw-lekulam">לכולם.ן</span>
        </span>
      </h2>

      <div className="cookie-content">
        <p className="cookie-text">
          <span className="cookie-text-he">
            אנו משתמשים בעוגיות ובטכנולוגיות דומות כדי לאסוף נתונים, ולהעניק חוויית גלישה טובה
            ומותאמת אישית. גלישה נעימה!
          </span>{' '}
          <span className="cookie-text-ar">
            نستخدم ملفات تعريف الارتباط (Cookies) وتقنيات مشابهة لجمع البيانات، وتوفير تجربة تصفح
            أفضل وأكثر ملاءمة لاحتياجاتك.
          </span>
        </p>
      </div>

      <button type="button" className="cookie-accept" onClick={() => close('accepted')}>
        אישור موافقة
      </button>
      </div>

      {/* Legal note: under the banner, over the overlay. White, centered. */}
      <div className="cookie-legal">
        <p>
          למידע נוסף ניתן לעיין ב
          <a
            className="cookie-legal-link"
            href="https://www.standing-together.org/takanon"
            target="_blank"
            rel="noopener noreferrer"
          >
            תקנון האתר
          </a>
        </p>
        <p>
          לשינוי הגדרות התאמה או סירוב{' '}
          <button type="button" className="cookie-legal-link" onClick={openCookieSettings}>
            לחצו כאן
          </button>
        </p>
      </div>
    </div>
  );
}
